import React, { useEffect, useState } from "react";
import { subscribePrefetchStatus } from "./NutriMartRoutes";

const LABELS: Record<string, { es: string; en: string }> = {
  nm_pf_dashboard: { es: "Inicio", en: "Dashboard" },
  nm_pf_products: { es: "Productos", en: "Products" },
};

/**
 * Discreet, non-blocking hint shown when a background prefetch has exhausted
 * its retries. The chunk will still be fetched lazily on navigation, so this
 * is informational only — never an error that blocks the UI.
 */
export function PrefetchStatusIndicator({ lang = "es" }: { lang?: "es" | "en" }) {
  const [failed, setFailed] = useState<string[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    return subscribePrefetchStatus((snap) => {
      const keys = Object.keys(snap).filter((k) => snap[k] === "failed");
      setFailed(keys);
      if (keys.length === 0) setDismissed(false);
    });
  }, []);

  if (dismissed || failed.length === 0) return null;

  const sections = failed.map((k) => LABELS[k]?.[lang] ?? k).join(", ");
  const msg = lang === "es"
    ? `Precarga no disponible (${sections}). Se cargará al navegar.`
    : `Preload unavailable (${sections}). Will load on navigation.`;
  const aria = lang === "es" ? "Cerrar aviso" : "Dismiss notice";

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 80,
        right: 12,
        zIndex: 40,
        maxWidth: 280,
        background: "hsl(var(--background) / 0.92)",
        color: "hsl(var(--muted-foreground))",
        border: "1px solid hsl(var(--border))",
        borderRadius: 10,
        padding: "6px 10px",
        fontFamily: "var(--f)",
        fontSize: 11,
        lineHeight: 1.3,
        display: "flex",
        alignItems: "center",
        gap: 8,
        backdropFilter: "blur(6px)",
        boxShadow: "0 4px 12px hsl(var(--foreground) / 0.08)",
      }}
    >
      <span aria-hidden="true" style={{ opacity: 0.7 }}>⏳</span>
      <span style={{ flex: 1 }}>{msg}</span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={aria}
        style={{
          background: "transparent",
          border: "none",
          color: "inherit",
          cursor: "pointer",
          fontSize: 13,
          opacity: 0.6,
          padding: 2,
        }}
      >
        ×
      </button>
    </div>
  );
}